<template>
  <div class="template-preview">
    <!-- Template selector (only if more than 1 template) -->
    <Dropdown
      v-if="templates.length > 1"
      :model-value="activeTemplateIndex"
      :options="dropdownOptions"
      :option-label="'label'"
      :option-value="'value'"
      :option-group-label="hasGroups ? 'label' : undefined"
      :option-group-children="hasGroups ? 'items' : undefined"
      class="template-preview__dropdown"
      @change="onDropdownChange"
    />

    <!-- Preview card -->
    <div
      v-if="activeTemplate"
      class="template-preview__card"
    >
      <!-- Toolbar -->
      <div class="template-preview__toolbar">
        <div class="template-preview__toolbar-left">
          <!-- Desktop / Mobile toggle -->
          <div class="template-preview__media-toggle">
            <button
              v-if="canShowDesktop(activeTemplate.type)"
              class="template-preview__media-btn"
              :class="{ 'template-preview__media-btn--active': activeMedia === 'desktop' }"
              @click="$emit('select-media', 'desktop')"
            >
              <i class="far fa-desktop" />
            </button>
            <button
              v-if="canShowMobile(activeTemplate.type)"
              class="template-preview__media-btn"
              :class="{ 'template-preview__media-btn--active': activeMedia === 'mobile' }"
              @click="$emit('select-media', 'mobile')"
            >
              <i class="far fa-mobile" />
            </button>
          </div>
        </div>

        <div class="template-preview__toolbar-right">
          <!-- HTML Preview button -->
          <button
            v-if="isImageReady"
            v-tooltip.left="t('automatedScenarios.actions.sending_channel.template_preview.htmlPreviewTooltip')"
            class="template-preview__action-btn"
            @click="openHtmlPreview"
          >
            <i class="fa-light fa-eye" />
          </button>

          <!-- Edit button -->
          <button
            v-tooltip.left="t('automatedScenarios.actions.stepper.editModel')"
            class="template-preview__action-btn"
            @click="handleEditTemplate"
          >
            <i class="fa-light fa-pen-to-square" />
          </button>

          <!-- Change template button -->
          <button
            v-tooltip.left="t('automatedScenarios.actions.stepper.changeModel')"
            class="template-preview__action-btn"
            @click="handleChangeTemplate"
          >
            <i class="fa-light fa-arrow-right-arrow-left" />
          </button>

          <!-- Send test (only for primary template when enabled) -->
          <button
            v-if="showSendTest && activeTemplateIndex === 0"
            v-tooltip.left="t('campaigns.common.operatorActions.sendTestMessage')"
            class="template-preview__action-btn"
            @click="$emit('send-test')"
          >
            <i class="fa-light fa-paper-plane" />
          </button>
        </div>
      </div>

      <!-- Preview image area -->
      <div class="template-preview__image-area">
        <!-- Hidden preloader image -->
        <img
          :key="imageRefreshKey"
          :src="getImageUrl(activeTemplate, false)"
          style="display: none"
          @load="$emit('image-loaded', activeTemplate.id_template)"
          @error="$emit('image-failed', activeTemplate.id_template)"
        >

        <!-- Desktop preview -->
        <div
          v-if="canShowDesktop(activeTemplate.type) && activeMedia === 'desktop'"
          class="template-preview__image template-preview__image--desktop"
        >
          <div
            v-if="isImageReady"
            class="template-preview__image-bg"
            :style="{ backgroundImage: `url(${getImageUrl(activeTemplate, false)})` }"
          />
          <div
            v-else
            class="template-preview__loading"
          >
            <ProgressSpinner
              style="width: 28px; height: 28px"
              stroke-width="6"
              fill="var(--surface-ground)"
              animation-duration="1.0s"
            />
          </div>
        </div>

        <!-- Mobile preview -->
        <div
          v-if="canShowMobile(activeTemplate.type) && activeMedia === 'mobile'"
          class="template-preview__image template-preview__image--mobile"
        >
          <div
            v-if="isImageReady"
            class="template-preview__image-bg"
            :style="{ backgroundImage: `url(${getImageUrl(activeTemplate, true)})` }"
          />
          <div
            v-else
            class="template-preview__loading"
          >
            <ProgressSpinner
              style="width: 28px; height: 28px"
              stroke-width="6"
              fill="var(--surface-ground)"
              animation-duration="1.0s"
            />
          </div>
        </div>
      </div>

      <!-- Template label -->
      <div class="template-preview__footer">
        <span class="template-preview__label">
          <i class="fa-light fa-file-lines" />
          {{ activeTemplate.label || `Template #${activeTemplate.id_template}` }}
        </span>
      </div>
    </div>

    <!-- HTML Preview Slider (teleported to body to escape sidebar overflow/stacking context) -->
    <Teleport to="body">
      <TemplateHtmlPreviewSlider
        v-if="htmlPreviewData && htmlPreviewData.previews.length > 0"
        :previews="htmlPreviewData.previews"
        @close="closeHtmlPreview"
      />
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import ProgressSpinner from 'primevue/progressspinner';
import Tooltip from 'primevue/tooltip';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { TEMPLATE_MEDIA_PREVIEW } from '@/composables/flows/useTemplateConfig';
import { UserState } from '@/composables/User';
import { isDisplayTemplate } from '@/components/template-builder/utils/helpers';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError } from '@/helpers';
import { store } from '@/store';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';

interface DropdownItem {
  label: string;
  value: number;
}

interface DropdownGroup {
  label: string;
  items: DropdownItem[];
}

/**
 * Extract game prize template IDs from root template's template_config.
 * Prize templateIds may be base64 encoded or raw numbers.
 */
const extractGamePrizeTemplateIds = (rootConfig: any): Set<number> => {
  const ids = new Set<number>();
  if (!rootConfig.hasGame || !rootConfig.games_data) return ids;

  const gameTypes = ['wheel', 'slotMachine', 'scratchCard'];
  for (const gameType of gameTypes) {
    const prizes = rootConfig.games_data[gameType]?.prizes;
    if (!prizes) continue;
    for (const prize of prizes) {
      if (!prize.templateId) continue;
      // templateId may be base64 encoded
      try {
        const decoded = Number(atob(String(prize.templateId)));
        if (!Number.isNaN(decoded) && decoded > 0) {
          ids.add(decoded);
          continue;
        }
      } catch { /* not base64, try raw */ }
      const raw = Number(prize.templateId);
      if (!Number.isNaN(raw) && raw > 0) {
        ids.add(raw);
      }
    }
  }
  return ids;
};

/**
 * Parse imageKey which may be a JSON array (new format) or a plain string (old format).
 */
const getFirstImageKey = (imageKey: string | undefined | null): string => {
  if (!imageKey) return '';
  try {
    const parsed = JSON.parse(imageKey);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed[0];
    }
  } catch {
    // Not JSON, return as is
  }
  return imageKey;
};

export default defineComponent({
  name: 'TemplatePreview',

  components: {
    Dropdown,
    Button,
    Menu,
    ProgressSpinner,
    TemplateHtmlPreviewSlider,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    templates: {
      type: Array as PropType<TemplatesCustom[]>,
      required: true,
      default: () => [],
    },

    activeMedia: {
      type: String as PropType<'desktop' | 'mobile'>,
      default: 'desktop',
    },

    activeTemplateIndex: {
      type: Number,
      default: 0,
    },

    imageLoadTime: {
      type: Object as PropType<Record<number, number>>,
      default: () => ({}),
    },

    imageRefreshKey: {
      type: Number,
      default: 0,
    },

    showSendTest: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'tab-change',
    'select-media',
    'change-template',
    'edit-template',
    'send-test',
    'image-loaded',
    'image-failed',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const menuRef = ref();
    const baseImagePreviewUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;
    const idShop = UserState.activeShop?.id || 0;

    // Get current active template
    const activeTemplate = computed(() => props.templates[props.activeTemplateIndex] || null);

    // Check if current image has loaded
    const isImageReady = computed(() => {
      if (!activeTemplate.value) return false;
      const time = props.imageLoadTime[activeTemplate.value.id_template];
      return !!time && time > 0;
    });

    // Build grouped dropdown options from templates list
    const dropdownOptions = computed((): DropdownItem[] | DropdownGroup[] => {
      const items: DropdownItem[] = props.templates.map((tmpl, index) => ({
        label: tmpl.label || `Template ${tmpl.id_template}`,
        value: index,
      }));

      if (props.templates.length <= 1) return items;

      // Check if root template has game data
      let rootConfig: any = {};
      try {
        rootConfig = JSON.parse(props.templates[0]?.template_config ?? '{}');
      } catch { /* invalid JSON */ }

      const gamePrizeIds = extractGamePrizeTemplateIds(rootConfig);

      // No game → flat list (no groups)
      if (gamePrizeIds.size === 0) return items;

      // With game → grouped
      const steps = items.filter((_, i) => !gamePrizeIds.has(props.templates[i].id_template));
      const gameResults = items.filter((_, i) => gamePrizeIds.has(props.templates[i].id_template));

      const groups: DropdownGroup[] = [];
      if (steps.length) {
        groups.push({
          label: t('automatedScenarios.actions.sending_channel.template_preview.steps'),
          items: steps,
        });
      }
      if (gameResults.length) {
        groups.push({
          label: t('automatedScenarios.actions.sending_channel.template_preview.gameResults'),
          items: gameResults,
        });
      }
      return groups;
    });

    // Whether the dropdown uses grouped options
    const hasGroups = computed(() => {
      const opts = dropdownOptions.value;
      return opts.length > 0 && 'items' in opts[0];
    });

    // Check if desktop preview is available for template type
    const canShowDesktop = (type: string | null | undefined): boolean => {
      if (!type) return false;
      return TEMPLATE_MEDIA_PREVIEW.desktop.includes(type);
    };

    // Check if mobile preview is available for template type
    const canShowMobile = (type: string | null | undefined): boolean => {
      if (!type) return false;
      return TEMPLATE_MEDIA_PREVIEW.mobile.includes(type);
    };

    // Get image URL for template preview (with getFirstImageKey support)
    const getImageUrl = (template: TemplatesCustom, isMobile: boolean): string => {
      const imageKey = getFirstImageKey(template.imageKey);
      const suffix = isDisplayTemplate(template.type ?? undefined) ? '-isdisplay' : '';
      const responsiveSuffix = isMobile ? '-responsive' : '';
      const width = isMobile ? 300 : 600;
      const timestamp = props.imageLoadTime[template.id_template] || new Date().getTime();
      return `${baseImagePreviewUrl}${idShop}_${imageKey}${suffix}${responsiveSuffix}.png&w=${width}&${timestamp}`;
    };

    // Toggle menu
    const toggleMenu = (event: Event) => {
      if (menuRef.value) {
        menuRef.value.toggle(event);
      }
    };

    // Handle dropdown selection change
    const onDropdownChange = (event: { value: number }) => {
      emit('tab-change', event.value);
    };

    // Action handlers
    const handleEditTemplate = () => {
      if (activeTemplate.value) {
        emit('edit-template', activeTemplate.value.id_template, activeTemplate.value.type, activeTemplate.value.label);
      }
    };

    const handleChangeTemplate = () => {
      if (activeTemplate.value) {
        emit('change-template', activeTemplate.value.id_template, activeTemplate.value.type);
      }
    };

    // ========================================================================
    // HTML Preview
    // ========================================================================
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);

    const openHtmlPreview = async () => {
      if (!activeTemplate.value) return;

      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const idTemplateB64 = btoa(String(activeTemplate.value.id_template));
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/html-preview-selector',
          {},
          '',
          { idShop, idTemplate: idTemplateB64 },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(result?.error || t('automatedScenarios.actions.sending_channel.template_preview.htmlPreviewError'));
        }
      } catch {
        showToastError(t('automatedScenarios.actions.sending_channel.template_preview.htmlPreviewError'));
      }
      store.commit('general/hideTheSpinner');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    return {
      t,
      menuRef,
      activeTemplate,
      isImageReady,
      dropdownOptions,
      hasGroups,
      canShowDesktop,
      canShowMobile,
      getImageUrl,
      toggleMenu,
      onDropdownChange,
      handleEditTemplate,
      handleChangeTemplate,
      htmlPreviewData,
      openHtmlPreview,
      closeHtmlPreview,
    };
  },
});
</script>

<style scoped lang="scss">
.template-preview {
  &__dropdown {
    width: 100%;
    margin-bottom: 0.75rem;
  }

  &__card {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
  }

  // ---- Toolbar ----
  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  &__toolbar-left,
  &__toolbar-right {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  // ---- Media toggle (desktop/mobile) ----
  &__media-toggle {
    display: flex;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
  }

  &__media-btn {
    width: 32px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #94a3b8;
    font-size: 0.8125rem;
    transition: all 0.15s ease;

    &:hover {
      color: #475569;
      background: #f1f5f9;
    }

    &--active {
      background: #94c748;
      color: #ffffff;

      &:hover {
        background: #85b53e;
        color: #ffffff;
      }
    }
  }

  // ---- Action buttons ----
  &__action-btn {
    width: 30px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    border-radius: 6px;
    cursor: pointer;
    color: #64748b;
    font-size: 0.8125rem;
    transition: all 0.15s ease;

    &:hover {
      color: #94c748;
      border-color: #94c748;
      background: #f7faf0;
    }
  }

  // ---- Image preview area ----
  &__image-area {
    position: relative;
    background: #f1f5f9;
    min-height: 200px;
  }

  &__image {
    &--desktop {
      min-height: 300px;
    }

    &--mobile {
      display: flex;
      justify-content: center;
      min-height: 250px;

      .template-preview__image-bg {
        max-width: 320px;
        margin: 0 auto;
      }
    }
  }

  &__image-bg {
    width: 100%;
    min-height: 300px;
    background-size: 100% auto;
    background-position: center top;
    background-repeat: no-repeat;
    transition: background-image 0.3s ease;

    &:hover {
      animation: UPANDDOWN 10s infinite;
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 250px;
    color: #94a3b8;
  }

  // ---- Footer ----
  &__footer {
    padding: 0.5rem 0.75rem;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #475569;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    i {
      color: #94a3b8;
      flex-shrink: 0;
    }
  }
}
</style>
